import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ColorLegend from "./ColorLegend";
import DataTable from "./DataTable";
import { styles } from "./DataTable.styles";
import ScreenRotationIcon from "@mui/icons-material/ScreenRotation";
import Alert from "@mui/material/Alert";
import { FONT_SIZES } from "../../constants/fontSizes";

const getDomainUtilityColor = (score) => {
  if (score < 2) return styles.highRiskColor; // Red - Not impressive
  if (score < 4) return styles.mediumRiskColor; // Orange - Great choice
  return styles.lowRiskColor; // Green - Amazing candidate
};

const tooltipStyles = {
  tooltip: {
    sx: {
      fontSize: FONT_SIZES.TEXT_HINT.xs,
      "@media (min-width: 600px)": {
        fontSize: FONT_SIZES.TEXT_HINT.sm,
      },
    },
  },
};

const columnTooltips = {
  seo_potential: "How likely is this domain to perform well in search engines?",
  clarity_readability:
    "Is the name easy to spell, say, and remember? Length of the domain name should be less than 10 characters for most favorable.",
  brand_strength: "Does the domain sound strong, trustworthy, and memorable?",
  functional_alignment: "Does the domain clearly match the app's purpose?",
};
const numericColumnWidth = "8%";

export const domainUtilityColumns = [
  {
    id: "name",
    label: "Candidate Name",
    width: "15%",
  },
  {
    id: "exact_domain_name",
    label: "Exact Domain Name",
    format: (value) => value || "",
    width: "15%",
  },
  {
    id: "seo_potential",
    label: "SEO Potential",
    width: numericColumnWidth,
    tooltip: columnTooltips.seo_potential,
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getDomainUtilityColor(parseFloat(value) || 0),
  },
  {
    id: "clarity_readability",
    label: "Clarity & Readability",
    width: numericColumnWidth,
    tooltip: columnTooltips.clarity_readability,
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getDomainUtilityColor(parseFloat(value) || 0),
  },
  {
    id: "brand_strength",
    label: "Brand Strength",
    width: numericColumnWidth,
    tooltip: columnTooltips.brand_strength,
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getDomainUtilityColor(parseFloat(value) || 0),
  },
  {
    id: "functional_alignment",
    label: "Functional Alignment",
    width: numericColumnWidth,
    tooltip: columnTooltips.functional_alignment,
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getDomainUtilityColor(parseFloat(value) || 0),
  },
  {
    id: "notes",
    label: "Notes",
    width: "20%",
    format: (value) => {
      if (!value) return "";
      return (
        <Tooltip
          title={value}
          placement="top"
          arrow
          enterDelay={200}
          leaveDelay={200}
          componentsProps={tooltipStyles}
        >
          <span style={styles.tooltipText}>
            {value.length > 100 ? `${value.substring(0, 100)}...` : value}
          </span>
        </Tooltip>
      );
    },
  },
];

const DomainUtilityReport = ({
  domainUtilityData,
  getDetailedRowColor,
  expandAccordian,
  onAccordionChange,
  isMobile,
  flipColumns,
  showUtilityReport,
}) => {
  if (!showUtilityReport) {
    return (
      <>
        <Typography variant="h6" sx={styles.reportTitle}>
          Domain Name Quality Report
        </Typography>
        <Alert severity="info" icon={<ScreenRotationIcon />} sx={styles.alert}>
          Rotate your device to landscape mode to view report table.
        </Alert>
      </>
    );
  }

  return (
    <Accordion
      expanded={expandAccordian}
      onChange={onAccordionChange}
      sx={styles.accordion}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" sx={styles.accordionTitle}>
          Domain Name Quality Report
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {isMobile ? (
          <Alert
            severity="info"
            icon={<ScreenRotationIcon />}
            sx={styles.alert}
          >
            Rotate your device to landscape mode to view report table.
          </Alert>
        ) : (
          <>
            <ColorLegend
              legendText={[
                "Amazing Candidate (4-5)",
                "Great Choice (2-3)",
                "Not Impressive (0-1)",
              ]}
              legendColor={[
                styles.lowRiskColor,
                styles.mediumRiskColor,
                styles.highRiskColor,
              ]}
            />
            <DataTable
              columns={domainUtilityColumns}
              data={domainUtilityData}
              getRowColor={getDetailedRowColor}
              isReportData={false}
              sx={styles.dataTable}
              isMobile={isMobile}
              flipColumns={flipColumns}
              isDomainUtility={true}
            />
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default DomainUtilityReport;
